var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import KeyCode from 'rc-util/lib/KeyCode';
import { composeRef } from 'rc-util/lib/ref';
import getMiniDecimal, { toFixed } from './utils/MiniDecimal';
import StepHandler from './StepHandler';
import { getNumberPrecision, num2str, validateNumber } from './utils/numberUtil';
import useCursor from './hooks/useCursor';
import useUpdateEffect from './hooks/useUpdateEffect';
import useFrame from './hooks/useFrame';
/**
 * We support `stringMode` which need handle correct type when user call in onChange
 * format max or min value
 * 1. if isInvalid return null
 * 2. if precision is undefined, return decimal
 * 3. format with precision
 *    I. if max > 0, round down with precision. Example: max= 3.5, precision=0  afterFormat: 3
 *    II. if max < 0, round up with precision. Example: max= -3.5, precision=0  afterFormat: -4
 *    III. if min > 0, round up with precision. Example: min= 3.5, precision=0  afterFormat: 4
 *    IV. if min < 0, round down with precision. Example: max= -3.5, precision=0  afterFormat: -3
 */
const getDecimalValue = (stringMode, decimalValue) => {
    if (stringMode || decimalValue.isEmpty()) {
        return decimalValue.toString();
    }
    return decimalValue.toNumber();
};
const getDecimalIfValidate = (value) => {
    const decimal = getMiniDecimal(value);
    return decimal.isInvalidate() ? null : decimal;
};
const InputNumber = React.forwardRef((props, ref) => {
    const { prefixCls = 'rc-input-number', className, style, min, max, step = 1, defaultValue, value, disabled, readOnly, upHandler, downHandler, keyboard, symmetryMode, controls = true, stringMode, parser, formatter, precision, decimalSeparator, onChange, onInput, onPressEnter, onStep } = props, inputProps = __rest(props, ["prefixCls", "className", "style", "min", "max", "step", "defaultValue", "value", "disabled", "readOnly", "upHandler", "downHandler", "keyboard", "symmetryMode", "controls", "stringMode", "parser", "formatter", "precision", "decimalSeparator", "onChange", "onInput", "onPressEnter", "onStep"]);
    const inputClassName = `${prefixCls}-input`;
    const inputRef = React.useRef(null);
    const [focus, setFocus] = React.useState(false);
    const userTypingRef = React.useRef(false);
    const compositionRef = React.useRef(false);
    // ============================ Value =============================
    // Real value control
    const [decimalValue, setDecimalValue] = React.useState(() => getMiniDecimal(value !== null && value !== void 0 ? value : defaultValue));
    function setUncontrolledDecimalValue(newDecimal) {
        if (value === undefined) {
            setDecimalValue(newDecimal);
        }
    }
    // ====================== Parser & Formatter ======================
    /**
     * `precision` is used for formatter & onChange.
     * It will auto generate by `value` & `step`.
     * But it will not block user typing.
     *
     * Note: Auto generate `precision` is used for legacy logic.
     * We should remove this since we already support high precision with BigInt.
     *
     * @param number  Provide which number should calculate precision
     * @param userTyping  Change by user typing
     */
    const getPrecision = React.useCallback((numStr, userTyping) => {
        if (userTyping) {
            return undefined;
        }
        if (precision >= 0) {
            return precision;
        }
        return Math.max(getNumberPrecision(numStr), getNumberPrecision(step));
    }, [precision, step]);
    // >>> Parser
    const mergedParser = React.useCallback((num) => {
        const numStr = String(num);
        if (parser) {
            return parser(numStr);
        }
        let parsedStr = numStr;
        if (decimalSeparator) {
            parsedStr = parsedStr.replace(decimalSeparator, '.');
        }
        // [Legacy] We still support auto convert `$ 123,456` to `123456`
        return parsedStr.replace(/[^\w.-]+/g, '');
    }, [parser, decimalSeparator]);
    // >>> Formatter
    const inputValueRef = React.useRef('');
    const mergedFormatter = React.useCallback((number, userTyping) => {
        if (formatter) {
            return formatter(number, { userTyping, input: String(inputValueRef.current) });
        }
        let str = typeof number === 'number' ? num2str(number) : number;
        // User typing will not auto format with precision directly
        if (!userTyping) {
            const mergedPrecision = getPrecision(str, userTyping);
            if (validateNumber(str) && (decimalSeparator || mergedPrecision >= 0)) {
                // Separator
                const separatorStr = decimalSeparator || '.';
                str = toFixed(str, separatorStr, mergedPrecision);
            }
        }
        return str;
    }, [formatter, getPrecision, decimalSeparator]);
    // ========================== InputValue ==========================
    /**
     * Input text value control
     *
     * User can not update input content directly. It update with follow rules by priority:
     *  1. controlled `value` changed
     *    * [SPECIAL] Typing like `1.` should not immediately convert to `1`
     *  2. User typing with format (not precision)
     *  3. Blur or Enter trigger revalidate
     */
    const [inputValue, setInternalInputValue] = React.useState(() => {
        const initValue = defaultValue !== null && defaultValue !== void 0 ? defaultValue : value;
        if (decimalValue.isInvalidate() && ['string', 'number'].includes(typeof initValue)) {
            return Number.isNaN(initValue) ? '' : initValue;
        }
        return mergedFormatter(decimalValue.toString(), false);
    });
    inputValueRef.current = inputValue;
    // Should always be string
    function setInputValue(newValue, userTyping) {
        setInternalInputValue(mergedFormatter(
        // Invalidate number is sometime passed by external control, we should let it go
        // Otherwise is controlled by internal interactive logic which check by userTyping
        // You can ref 'show limited value when input is not focused' test for more info.
        newValue.isInvalidate() ? newValue.toString(false) : newValue.toString(!userTyping), userTyping));
    }
    // >>> Max & Min limit
    const maxDecimal = React.useMemo(() => getDecimalIfValidate(max), [max]);
    const minDecimal = React.useMemo(() => getDecimalIfValidate(min), [min]);
    const upDisabled = React.useMemo(() => {
        if (!maxDecimal || !decimalValue || decimalValue.isInvalidate()) {
            return false;
        }
        return maxDecimal.lessEquals(decimalValue);
    }, [maxDecimal, decimalValue]);
    const downDisabled = React.useMemo(() => {
        if (!minDecimal || !decimalValue || decimalValue.isInvalidate()) {
            return false;
        }
        return decimalValue.lessEquals(minDecimal);
    }, [minDecimal, decimalValue]);
    // Cursor controller
    const [recordCursor, restoreCursor] = useCursor(inputRef.current, focus);
    // ============================= Data =============================
    /**
     * Find target value closet within range.
     * e.g. [11, 28]:
     *    3  => 11
     *    23 => 23
     *    99 => 28
     */
    const getRangeValue = (target) => {
        // target > max
        if (maxDecimal && !target.lessEquals(maxDecimal)) {
            return maxDecimal;
        }
        // target < min
        if (minDecimal && !minDecimal.lessEquals(target)) {
            return minDecimal;
        }
        return null;
    };
    /**
     * Check value is in [min, max] range
     */
    const isInRange = (target) => !getRangeValue(target);
    /**
     * Trigger `onChange` if value validated and not equals of origin.
     * Return the value that re-align in range.
     */
    const triggerValueUpdate = (newValue, userTyping) => {
        let updateValue = newValue;
        let isRangeValidate = isInRange(updateValue) || updateValue.isEmpty();
        // Skip align value when trigger value is empty.
        // We just trigger onChange(null)
        // This should not block user typing
        if (!updateValue.isEmpty() && !userTyping) {
            // Revert value in range if needed
            updateValue = getRangeValue(updateValue) || updateValue;
            isRangeValidate = true;
        }
        if (!readOnly && !disabled && isRangeValidate) {
            const numStr = updateValue.toString();
            const mergedPrecision = getPrecision(numStr, userTyping);
            if (mergedPrecision >= 0) {
                updateValue = getMiniDecimal(toFixed(numStr, '.', mergedPrecision));
            }
            // Trigger event
            if (!updateValue.equals(decimalValue)) {
                setUncontrolledDecimalValue(updateValue);
                onChange === null || onChange === void 0 ? void 0 : onChange(updateValue.isEmpty() ? null : getDecimalValue(stringMode, updateValue));
                // Reformat input if value is not controlled
                if (value === undefined) {
                    setInputValue(updateValue, userTyping);
                }
            }
            return updateValue;
        }
        return decimalValue;
    };
    // ========================== User Input ==========================
    const onNextPromise = useFrame();
    // >>> Collect input value
    const collectInputValue = (inputStr) => {
        recordCursor();
        // Update inputValue incase input can not parse as number
        setInternalInputValue(inputStr);
        // Parse number
        if (!compositionRef.current) {
            const finalValue = mergedParser(inputStr);
            const finalDecimal = getMiniDecimal(finalValue);
            if (!finalDecimal.isNaN()) {
                triggerValueUpdate(finalDecimal, true);
            }
        }
        // Trigger onInput later to let user customize value if they want do handle something after onChange
        onInput === null || onInput === void 0 ? void 0 : onInput(inputStr);
        // optimize for chinese input experience
        // https://github.com/ant-design/ant-design/issues/8196
        onNextPromise(() => {
            let nextInputStr = inputStr;
            if (!parser) {
                nextInputStr = inputStr.replace(/。/g, '.');
            }
            if (nextInputStr !== inputStr) {
                collectInputValue(nextInputStr);
            }
        });
    };
    // >>> Composition
    const onCompositionStart = () => {
        compositionRef.current = true;
    };
    const onCompositionEnd = () => {
        compositionRef.current = false;
        collectInputValue(inputRef.current.value);
    };
    // >>> Input
    const onInternalInput = e => {
        collectInputValue(e.target.value);
    };
    // ============================= Step =============================
    const onInternalStep = (up) => {
        var _a;
        // Ignore step since out of range
        if ((up && upDisabled) || (!up && downDisabled)) {
            return;
        }
        // Clear typing status since it may caused by up & down key.
        // We should sync with input value.
        userTypingRef.current = false;
        let stepDecimal = getMiniDecimal(step);
        if (!up) {
            stepDecimal = stepDecimal.negate();
        }
        const target = (decimalValue || getMiniDecimal(0)).add(stepDecimal.toString());
        const updatedValue = triggerValueUpdate(target, false);
        onStep === null || onStep === void 0 ? void 0 : onStep(getDecimalValue(stringMode, updatedValue), {
            offset: step,
            type: up ? 'up' : 'down'
        });
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    // ============================ Flush =============================
    /**
     * Flush current input content to trigger value change & re-formatter input if needed
     */
    const flushInputValue = (userTyping) => {
        const parsedValue = getMiniDecimal(mergedParser(inputValue));
        let formatValue = parsedValue;
        if (!parsedValue.isNaN()) {
            // Only validate value or empty value can be re-fill to inputValue
            // Reassign the formatValue within ranged of trigger control
            formatValue = triggerValueUpdate(parsedValue, userTyping);
        }
        else {
            formatValue = decimalValue;
        }
        if (value !== undefined) {
            // Reset back with controlled value first
            setInputValue(decimalValue, false);
        }
        else if (!formatValue.isNaN()) {
            // Reset input back since no validate value
            setInputValue(formatValue, false);
        }
    };
    const onKeyDown = event => {
        const { which } = event;
        userTypingRef.current = true;
        if (which === KeyCode.ENTER) {
            if (!compositionRef.current) {
                userTypingRef.current = false;
            }
            flushInputValue(false);
            onPressEnter === null || onPressEnter === void 0 ? void 0 : onPressEnter(event);
        }
        if (keyboard === false) {
            return;
        }
        // Do step
        if (!compositionRef.current && [KeyCode.UP, KeyCode.DOWN].includes(which)) {
            onInternalStep(KeyCode.UP === which);
            event.preventDefault();
        }
    };
    const onKeyUp = () => {
        userTypingRef.current = false;
    };
    // >>> Focus & Blur
    const onBlur = () => {
        flushInputValue(false);
        setFocus(false);
        userTypingRef.current = false;
    };
    // ========================== Controlled ==========================
    // Input by precision
    useUpdateEffect(() => {
        if (!decimalValue.isInvalidate()) {
            setInputValue(decimalValue, false);
        }
    }, [precision]);
    // Input by value
    useUpdateEffect(() => {
        const newValue = getMiniDecimal(value);
        setDecimalValue(newValue);
        const currentParsedValue = getMiniDecimal(mergedParser(inputValue));
        // When user typing from `1.2` to `1.`, we should not convert to `1` immediately.
        // But let it go if user set `formatter`
        if (!newValue.equals(currentParsedValue) || !userTypingRef.current || formatter) {
            // Update value as effect
            setInputValue(newValue, userTypingRef.current);
        }
    }, [value]);
    // ============================ Cursor ============================
    useUpdateEffect(() => {
        if (formatter) {
            restoreCursor();
        }
    }, [inputValue]);
    // ============================ Render ============================
    return (<div className={classNames(prefixCls, className, {
            [`${prefixCls}-focused`]: focus,
            [`${prefixCls}-disabled`]: disabled,
            [`${prefixCls}-readonly`]: readOnly,
            [`${prefixCls}-not-a-number`]: decimalValue.isNaN(),
            [`${prefixCls}-out-of-range`]: !decimalValue.isInvalidate() && !isInRange(decimalValue)
        })} style={style} onFocus={() => {
            setFocus(true);
        }} onBlur={onBlur} onKeyDown={onKeyDown} onKeyUp={onKeyUp} onCompositionStart={onCompositionStart} onCompositionEnd={onCompositionEnd}>
            {controls && (<StepHandler prefixCls={prefixCls} upNode={upHandler} downNode={downHandler} upDisabled={upDisabled} downDisabled={downDisabled} symmetryMode={symmetryMode} onStep={onInternalStep}/>)}
            <div className={`${inputClassName}-wrap`}>
                <input autoComplete="off" role="spinbutton" aria-valuemin={min} aria-valuemax={max} aria-valuenow={decimalValue.isInvalidate() ? null : decimalValue.toString()} step={step} {...inputProps} ref={composeRef(inputRef, ref)} className={inputClassName} value={inputValue} onChange={onInternalInput} disabled={disabled} readOnly={readOnly}/>
            </div>
        </div>);
});
InputNumber.displayName = 'InputNumber';
export default InputNumber;
